import React from "react";
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Divider,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Grid,
  Heading,
  Input,
  Radio,
  RadioGroup,
  Stack,
  Text,
} from "@chakra-ui/react";
import { formatAddress } from "../helpers";
import { AddressMode, Homeowner, NewAddressInput } from "../types";
import { RequestCreateValidationErrors } from "../validation";

type RequestAddressSectionProps = {
  homeowner: Homeowner | null;
  isLoading: boolean;
  addressMode: AddressMode;
  selectedAddressId: string;
  newAddress: NewAddressInput;
  addressSearch: string;
  addressPredictions: google.maps.places.AutocompletePrediction[];
  addressSearchLoading: boolean;
  googlePlacesAvailable: boolean;
  errors?: Pick<RequestCreateValidationErrors, "address" | "newAddress">;
  onAddressModeChange: (mode: AddressMode) => void;
  onSavedAddressChange: (addressId: string) => void;
  onNewAddressChange: (patch: Partial<NewAddressInput>) => void;
  onAddressSearch: (value: string) => void;
  onAddressPredictionClick: (
    prediction: google.maps.places.AutocompletePrediction
  ) => void;
};

export const RequestAddressSection = ({
  homeowner,
  isLoading,
  addressMode,
  selectedAddressId,
  newAddress,
  addressSearch,
  addressPredictions,
  addressSearchLoading,
  googlePlacesAvailable,
  errors,
  onAddressModeChange,
  onSavedAddressChange,
  onNewAddressChange,
  onAddressSearch,
  onAddressPredictionClick,
}: RequestAddressSectionProps) => {
  const hasSavedAddresses = Boolean(homeowner?.addresses?.length);

  return (
    <Box bg="white" borderWidth="1px" borderColor="gray.200" rounded="md" p={5}>
      <Heading size="sm" mb={4}>
        Request address
      </Heading>
      {!homeowner ? (
        <Text color="gray.500">Select a homeowner before choosing an address.</Text>
      ) : isLoading ? (
        <Text color="gray.500">Loading homeowner addresses...</Text>
      ) : (
        <Stack spacing={4}>
          <FormControl isInvalid={Boolean(errors?.address)}>
            <RadioGroup
              aria-label="Request address"
              value={addressMode === "saved" ? selectedAddressId : "new"}
              onChange={(value) => {
                if (value === "new") {
                  onAddressModeChange("new");
                  return;
                }

                onAddressModeChange("saved");
                onSavedAddressChange(value);
              }}
            >
              <Stack spacing={3}>
                {homeowner.addresses?.map((address) => (
                  <Radio key={address.id} value={address.id}>
                    {formatAddress(address)}
                  </Radio>
                ))}
                <Radio value="new">Add a new address</Radio>
              </Stack>
            </RadioGroup>
            <FormErrorMessage>{errors?.address}</FormErrorMessage>
          </FormControl>

          {!hasSavedAddresses && (
            <Alert status="info" rounded="md">
              <AlertIcon />
              This homeowner has no saved addresses yet.
            </Alert>
          )}

          {addressMode === "new" && (
            <Stack spacing={3}>
              <Divider />
              <FormControl>
                <FormLabel htmlFor="new-address-search">Find address</FormLabel>
                <Input
                  id="new-address-search"
                  value={addressSearch}
                  onChange={(event) => onAddressSearch(event.target.value)}
                  placeholder="Search Google Places"
                  isDisabled={!googlePlacesAvailable}
                />
                {!googlePlacesAvailable && (
                  <Text color="gray.500" fontSize="sm" mt={1}>
                    Google address search is unavailable. Enter the address manually.
                  </Text>
                )}
              </FormControl>
              {addressSearchLoading && <Text color="gray.500">Searching addresses...</Text>}
              {addressPredictions.length > 0 && (
                <Stack spacing={2}>
                  {addressPredictions.map((prediction) => (
                    <Button
                      key={prediction.place_id}
                      variant="ghost"
                      justifyContent="flex-start"
                      whiteSpace="normal"
                      h="auto"
                      py={3}
                      onClick={() => onAddressPredictionClick(prediction)}
                    >
                      <Box textAlign="left">
                        <Text>{prediction.structured_formatting.main_text}</Text>
                        <Text fontSize="sm" color="gray.500">
                          {prediction.structured_formatting.secondary_text}
                        </Text>
                      </Box>
                    </Button>
                  ))}
                </Stack>
              )}
              <FormControl isRequired isInvalid={Boolean(errors?.newAddress?.address)}>
                <FormLabel htmlFor="new-address-line">Address line</FormLabel>
                <Input
                  id="new-address-line"
                  value={newAddress.address}
                  onChange={(event) =>
                    onNewAddressChange({
                      address: event.target.value,
                      formattedAddress: newAddress.formattedAddress || event.target.value,
                    })
                  }
                />
                <FormErrorMessage>{errors?.newAddress?.address}</FormErrorMessage>
              </FormControl>
              <FormControl
                isRequired
                isInvalid={Boolean(errors?.newAddress?.formattedAddress)}
              >
                <FormLabel htmlFor="new-formatted-address">Formatted address</FormLabel>
                <Input
                  id="new-formatted-address"
                  value={newAddress.formattedAddress}
                  onChange={(event) =>
                    onNewAddressChange({ formattedAddress: event.target.value })
                  }
                />
                <FormErrorMessage>
                  {errors?.newAddress?.formattedAddress}
                </FormErrorMessage>
              </FormControl>
              <Grid templateColumns={{ base: "1fr", md: "1fr 1fr" }} gap={3}>
                <FormControl isRequired isInvalid={Boolean(errors?.newAddress?.city)}>
                  <FormLabel htmlFor="new-address-city">City</FormLabel>
                  <Input
                    id="new-address-city"
                    value={newAddress.city}
                    onChange={(event) => onNewAddressChange({ city: event.target.value })}
                  />
                  <FormErrorMessage>{errors?.newAddress?.city}</FormErrorMessage>
                </FormControl>
                <FormControl isRequired isInvalid={Boolean(errors?.newAddress?.state)}>
                  <FormLabel htmlFor="new-address-state">State</FormLabel>
                  <Input
                    id="new-address-state"
                    value={newAddress.state}
                    onChange={(event) => onNewAddressChange({ state: event.target.value })}
                  />
                  <FormErrorMessage>{errors?.newAddress?.state}</FormErrorMessage>
                </FormControl>
              </Grid>
            </Stack>
          )}
        </Stack>
      )}
    </Box>
  );
};
